// LineItemLLP.js — Quaterly: PPR line items vs Locked Labor Plan.
//
// Rows: Receive Dock, Receive Support, RSR Support, Transfer Out, Transfer Out Dock,
// each expandable to its functions (material buckets) with >=10 hrs on the day.
//
// LLP (line):  ALPS PPR_LINE_ITEM/SHIFT (ALPS_LI_<site>): SHOW_HOURS, VETERAN_RATE (UPH),
//              WEIGHTED_AVERAGE_DENSITY. LLP CPLH = UPH / WAD.
//   Transfer Out = TO Fluid Load + TO RWC + TO Palletize + TO Pallet Verify +
//              Transfer Out Support hours; CPLH = planned OB cartons / those hours
//              (planned OB cartons = Transfer Out Dock hours x CPLH).
//   RSR Support: no ALPS rate -> UPH = LVP planned cartons x WAD x 2 / RSR hours; CPLH = UPH / WAD.
// LLP (functions): LVP plan for the week — planned hours (computedHours) and rate =
//              planned cartons of the function's bucket / planned hours (as on Hourly).
// Actuals:     FCLM via the 24hr payload — process_hours (line) and labor_by_process
//              (functions), cartons inbound/outbound/bucket over the same window.
// Window:      the tab's Timeframe (00:00 -> checkpoint). Shift LLP is prorated by the
//              window's share of each shift's BRT minutes.
import React, { useEffect, useMemo, useState } from 'react';

const LINES = [
  { key: 'rd', label: 'Receive Dock', alps: ['Receive Dock'], procs: ['Receive Dock'], base: 'ib' },
  { key: 'rs', label: 'Receive Support', alps: ['Receive Support'], procs: ['Receive Support'], base: 'ib' },
  { key: 'rsr', label: 'RSR Support', alps: ['RSR Support'], procs: ['RSR Support'], base: 'both', rsr: true },
  { key: 'to', label: 'Transfer Out', alps: ['TO Fluid Load', 'TO RWC', 'TO Palletize', 'TO Pallet Verify', 'Transfer Out Support'],
    procs: ['TO Fluid Load', 'TO RWC', 'TO Palletize', 'Transfer Out Support'], base: 'ob', combined: true },
  { key: 'tod', label: 'Transfer Out Dock', alps: ['Transfer Out Dock'], procs: ['Transfer Out Dock'], base: 'ob' },
];
// "Show me everything": every PPR line item in display order. Core lines (above, with
// function dropdowns) are referenced by key; the rest are flat rows.
//   llp: 'line' = ALPS line item (UPH / WAD) | 'ob' = ALPS hours, CPLH = planned OB cartons / hours
//        'main' = ALPS main process (INBOUND / DA) | 'prod' = INBOUND + DA | 'thru' = INBOUND + DA + LVP admin
//        'admin' = LVP admin hours, CPLH = planned OB cartons / hours | 'none'
//   Actuals: the PPR row (volume / hours); CPLH = PPR volume / hours / WAD.
const EVERYTHING = [
  { core: 'rd' },
  { label: 'LP Receive', ppr: 'ppr.detail.inbound.receive.lpReceive', llp: 'line', alps: 'LP Receive', wad: 'ib' },
  { label: 'Pallet Receive', ppr: 'ppr.detail.inbound.receive.palletReceive', llp: 'line', alps: 'Pallet Receive', wad: 'ib' },
  { core: 'rs' }, { core: 'rsr' },
  { label: 'IB Lead/PA', ppr: 'ppr.detail.inbound.ibLeadPa.ibLeadPa', llp: 'line', alps: 'IB Lead/PA', wad: 'ib' },
  { label: 'IB Problem Solve', ppr: 'ppr.detail.inbound.ibDefect.ibProblemSolve', llp: 'line', alps: 'IB Problem Solve', wad: 'ib' },
  { core: 'to' }, { core: 'tod' },
  { label: 'TO Lead/PA', ppr: 'ppr.detail.da.toLeadPa.toLeadPa', llp: 'line', alps: 'TO Lead/PA', wad: 'ob' },
  { label: 'TO Problem Solve', ppr: 'ppr.detail.da.toDefect.toProblemSolve', llp: 'line', alps: 'TO Problem Solve', wad: 'ob' },
  { label: 'DA Bldg to Bldg Transfer TOTAL', ppr: 'ppr.detail.da.da.total', llp: 'ob', alps: 'DA Bldg to Bldg Transfer TOTAL', wad: 'ob' },
  { label: 'Admin/HR/IT', ppr: 'ppr.detail.support.support.adminHRIT', llp: 'admin', wad: 'ob' },
  { label: 'Total Inbound', ppr: 'ppr.fcSummary.totalInbound', llp: 'main', main: 'INBOUND', wad: 'ib' },
  { label: 'Transfer-Out (FC Summary)', ppr: 'ppr.fcSummary.transferOut', llp: 'main', main: 'DA', wad: 'ob' },
  { label: 'Support', ppr: 'ppr.fcSummary.support', llp: 'admin', wad: 'ob' },
  { label: 'THROUGHPUT', ppr: 'ppr.fcSummary.throughput', llp: 'thru', wad: 'blend' },
  { label: 'Production Only Throughput', pprMatch: /^ppr\.fcSummary\..*production/i, llp: 'prod', wad: 'blend' },
];

// Function (FCLM path) -> carton bucket, as on the Hourly tab. Unlisted -> the line's base.
const PATH_BUCKET = {
  'PID Unload': 'pid', 'PID Divert': 'pid', 'PID Reinject': 'pid', 'PID Inject': 'pid', 'PID Operator': 'pid', 'Jam Clear': 'pid',
  'AROS Unload': 'aros', 'AROS Operator': 'aros', 'AROS CSX Label': 'aros', 'AROS Avery': 'aros', 'AROS Divert': 'aros', 'AROS Sort': 'aros', 'AROS Waterspider': 'aros',
  'PR Unload': 'pr', 'Pallet Build': 'pr', 'Wrap/Lantech': 'pr', 'PR Waterspider': 'pr', 'IB Pallet EPJ WS': 'pr', 'Pallet Auditor': 'pr', 'EPJ Spotter': 'pr',
  'PIT Unloader': 'pr', 'PIT Pallet Build': 'pr', 'IB PIT Waterspider': 'pr',
  'Sorter Wallbuild': 'fluidDirect', 'Fluid WS': 'fluidOffline', 'Fluid Load': 'fluidOffline', 'Offline Wallbuild': 'fluidOffline',
  'EOL Avery': 'eol', 'EOL Divert': 'eol', 'EOL Sort': 'eol', 'EOL Waterspider': 'eol', 'Re-inject': 'eol',
  'OB EPJ WaterSpider': 'pit', 'Pallet Loader': 'pit', 'OB PIT Operator': 'pit', 'OB EPJ Operator': 'pit',
};
// FCLM path -> LVP computedHours key, where the names differ.
const LVP_KEY = {
  'Wrap/Lantech': 'Pallet Wrap/Lantech', 'Pallet Auditor': 'Pallet Auditors', 'Sorter Wallbuild': 'Sorter Wallbuilder',
  'Offline Wallbuild': 'Wall Builder', 'Re-inject': 'Trans Out Overflow', 'EPJ Spotter': 'IB Pallet EPJ WS', 'AROS Sort': 'IDRT AROS',
};
const lvpKeyFor = (path, proc) => (path === 'EOL Sort' ? (proc === 'RSR Support' ? 'IDRT EOL' : null) : (LVP_KEY[path] || path));
// Shift boundaries (site-local), to assign 24hr payload hour labels to shifts.
const SHIFT_START = { OLM1: { day: 8 * 60, night: 19 * 60 }, SBD3: { day: 7 * 60, night: 18 * 60 + 30 }, SCK8: { day: 7 * 60, night: 18 * 60 } };
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_WAD = { ib: 1.48, ob: 1.50 };

const isoAdd = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const sundayOf = iso => isoAdd(iso, -new Date(`${iso}T12:00:00`).getDay());
const lvpShiftKey = (iso, s) => `${DAYS[new Date(`${iso}T12:00:00`).getDay()]}_${s}`;
const toMin = l => { const [h, m] = String(l).split(':').map(Number); return h * 60 + (m || 0); };
const sum = a => a.reduce((x, y) => x + (y || 0), 0);

// Planned carton buckets for one LVP shift (same formulas as the Hourly tab).
function lvpBuckets(plan, shiftKey) {
  const total = Number(plan?.shiftData?.[shiftKey]?.cartons) || 0;
  if (!total) return null;
  const fpp = plan.fppRatios || {}, sd = plan.shiftData[shiftKey];
  const eolPct = (plan.eolPct || 33) / 100;
  const pid = sd.pidCartons ? parseInt(sd.pidCartons, 10) : Math.round(total * (fpp.pid || 0));
  const aros = sd.arosCartons ? parseInt(sd.arosCartons, 10) : Math.round(total * (fpp.aros || 0));
  const pr = Math.round(total * (fpp.pr || 0)) + Math.round(total * (fpp.skid || 0));
  const fluidDirect = Math.round(pid * (1 - eolPct));
  return { ib: total, ob: total, both: total * 2, pid, aros, pr, pit: pr, eol: Math.round(pid * eolPct), fluidDirect, fluidOffline: Math.max(0, pid + aros - fluidDirect) };
}

export default function LineItemLLP({ api, site, date, endMin, data }) {
  const [alpsLi, setAlpsLi] = useState(null);
  const [lvp, setLvp] = useState({});
  const [open, setOpen] = useState({});
  const [everything, setEverything] = useState(false);
  const [alpsMain, setAlpsMain] = useState(null);

  useEffect(() => {
    let alive = true;
    const get = (d, st) => fetch(`${api}/api/fclm/cache?site=${site}&date=${d}&shift_type=${st}`).then(r => (r.ok ? r.json() : null)).catch(() => null);
    const weeks = [...new Set([sundayOf(date), sundayOf(isoAdd(date, -1))])];
    Promise.all([get(`ALPS_LI_${site}`, 'data'), get(`ALPS_${site}`, 'data'), ...weeks.map(w => get(`LVP_${w}`, 'plan'))]).then(([a, am, ...plans]) => {
      if (!alive) return;
      setAlpsMain(am && Array.isArray(am.rows) ? am.rows : []);
      setAlpsLi(a && Array.isArray(a.rows) ? a.rows : []);
      const m = {}; weeks.forEach((w, i) => { if (plans[i] && plans[i].computedHours) m[w] = plans[i]; }); setLvp(m);
    });
    return () => { alive = false; };
  }, [api, site, date]);

  const model = useMemo(() => {
    if (!data || !alpsLi) return null;
    const labels = data.hour_labels || [];
    const sched = data.schedule || [];
    const st = SHIFT_START[site] || SHIFT_START.OLM1;
    // Which shift each 24hr label belongs to: 'prev' (previous date's night tail), 'day', 'night'.
    const shiftOf = l => { const m = toMin(l); return m >= st.night ? 'night' : m < st.day ? 'prev' : 'day'; };
    const inWin = l => toMin(l) < endMin;
    const brt = i => (sched[i] && sched[i].brt) || 0;
    const tot = { day: 0, night: 0 }, win = { prev: 0, day: 0, night: 0 };
    labels.forEach((l, i) => { const s = shiftOf(l); tot[s === 'day' ? 'day' : 'night'] += brt(i); if (inWin(l)) win[s] += brt(i); });
    const frac = { prev: tot.night ? win.prev / tot.night : 0, day: tot.day ? win.day / tot.day : 0, night: tot.night ? win.night / tot.night : 0 };
    const parts = [ // [fraction, alpsDate, ALPS shift, LVP iso date, LVP shift]
      [frac.prev, isoAdd(date, -1), 'NIGHT_SHIFT', isoAdd(date, -1), 'night'],
      [frac.day, date, 'DAY_SHIFT', date, 'day'],
      [frac.night, date, 'NIGHT_SHIFT', date, 'night'],
    ].filter(p => p[0] > 0);
    const A = {};
    for (const r of alpsLi) ((A[`${r.date}|${r.shift}|${r.lineItem}`] = A[`${r.date}|${r.shift}|${r.lineItem}`] || {})[r.concept] = r.value);
    const alpsCell = (d, s, li) => A[`${d}|${s}|${li}`] || {};
    const cph = (c, base) => (c.VETERAN_RATE ? c.VETERAN_RATE / (c.WEIGHTED_AVERAGE_DENSITY || DEFAULT_WAD[base] || 1.5) : null);
    const lvpPlanFor = iso => lvp[sundayOf(iso)];

    // Actuals over the window.
    const winLabels = labels.filter(inWin);
    const allLabels = labels;
    const cartonsOver = (bucket, ls) => {
      const C = data.cartons || {};
      const h = k => sum(ls.map(l => C[k]?.hours?.[l] || 0));
      return bucket === 'ib' ? h('inbound') : bucket === 'ob' ? h('outbound') : bucket === 'both' ? h('inbound') + h('outbound') : h(bucket);
    };
    const PH = data.process_hours || {}, LBP = data.labor_by_process || {};
    const hasActuals = Object.keys(PH).length > 0;

    const rows = LINES.map(line => {
      // ---- LLP (line) ----
      let llpH = 0, llpC = 0;
      for (const [f, ad, as, ld, ls] of parts) {
        let H, rate;
        if (line.combined) {
          H = sum(line.alps.map(li => alpsCell(ad, as, li).SHOW_HOURS));
          const tod = alpsCell(ad, as, 'Transfer Out Dock');
          const obCartons = (tod.SHOW_HOURS || 0) * (cph(tod, 'ob') || 0);
          rate = H ? obCartons / H : null;
        } else {
          const c = alpsCell(ad, as, line.alps[0]);
          H = c.SHOW_HOURS || 0;
          if (line.rsr) {
            const b = lvpBuckets(lvpPlanFor(ld), lvpShiftKey(ld, ls));
            const wad = c.WEIGHTED_AVERAGE_DENSITY || DEFAULT_WAD.ib;
            const uph = b && H ? (b.ib * wad * 2) / H : null;      // planned units x 2 / hours
            rate = uph != null ? uph / wad : null;                 // -> CPLH
          } else rate = cph(c, line.base);
        }
        llpH += H * f;
        if (rate != null) llpC += H * f * rate;
      }
      const llpCplh = llpH > 0 && llpC > 0 ? llpC / llpH : null;
      // ---- Actual (line) ----
      const actH = sum(line.procs.map(p => sum(winLabels.map(l => PH[p]?.[l] || 0))));
      const actC = cartonsOver(line.base, winLabels);
      const actCplh = actH > 0 ? actC / actH : null;

      // ---- Functions ----
      const paths = new Map();   // path -> {procs:Set}
      for (const p of line.procs) for (const path of Object.keys(LBP[p] || {})) {
        if (!paths.has(path)) paths.set(path, new Set());
        paths.get(path).add(p);
      }
      const subs = [];
      for (const [path, procs] of paths) {
        const hrsOver = ls => sum([...procs].map(p => sum(ls.map(l => LBP[p]?.[path]?.[l] || 0))));
        const dayAct = hrsOver(allLabels), wAct = hrsOver(winLabels);
        const bucket = PATH_BUCKET[path] || line.base;
        let pH = 0, pC = 0, dayLvp = 0, anyKey = false;
        for (const [f, , , ld, ls] of parts) {
          const plan = lvpPlanFor(ld); if (!plan) continue;
          const sk = lvpShiftKey(ld, ls);
          const k = lvpKeyFor(path, [...procs][0]);
          const h = k ? Number(plan.computedHours?.[k]?.[sk]) || 0 : 0;
          if (k && plan.computedHours?.[k]) anyKey = true;
          const b = lvpBuckets(plan, sk);
          pH += h * f;
          if (b && h) pC += b[bucket] * f;           // planned cartons for the bucket over this part
        }
        for (const [iso, s] of [[isoAdd(date, -1), 'night'], [date, 'day'], [date, 'night']]) {
          const plan = lvpPlanFor(iso); const k = lvpKeyFor(path, [...procs][0]);
          if (plan && k) dayLvp += Number(plan.computedHours?.[k]?.[lvpShiftKey(iso, s)]) || 0;
        }
        if (Math.max(dayAct, dayLvp) < 10) continue;
        const aC = cartonsOver(bucket, winLabels);
        subs.push({ path, llpH: anyKey ? pH : null, llpCplh: anyKey && pH > 0 ? pC / pH : null, actH: wAct, actCplh: wAct > 0 ? aC / wAct : null });
      }
      subs.sort((a, b) => (b.actH || 0) - (a.actH || 0));
      return { ...line, llpH, llpCplh, actH, actCplh, subs };
    });
    // ---- "Show me everything" flat rows ----
    const M = {};
    for (const r of alpsMain || []) if (r.aspect === 'FORECAST') ((M[`${r.date}|${r.shift}|${r.mainProcess}`] = M[`${r.date}|${r.shift}|${r.mainProcess}`] || {})[r.concept] = r.value);
    const mainCell = (d, s, mp) => M[`${d}|${s}|${mp}`] || {};
    const obPlanCartons = (ad, as) => { const t = alpsCell(ad, as, 'Transfer Out Dock'); return (t.SHOW_HOURS || 0) * (cph(t, 'ob') || 0); };
    const adminH = (ld, ls) => Number(lvpPlanFor(ld)?.adminHours?.[lvpShiftKey(ld, ls)]) || 0;
    const PL = data.ppr_lines || {};
    const pprIds = Object.keys(PL);
    const wadOf = (r, ad, as) => {
      if (r.wad === 'blend') {
        const ib = mainCell(ad, as, 'INBOUND'), da = mainCell(ad, as, 'DA');
        const ibC = (ib.SHOW_HOURS || 0) * (ib.CPLH || 0), daC = (da.SHOW_HOURS || 0) * (da.CPLH || 0);
        return ibC + daC > 0 ? (ibC * (ib.WEIGHTED_AVERAGE_DENSITY || DEFAULT_WAD.ib) + daC * (da.WEIGHTED_AVERAGE_DENSITY || DEFAULT_WAD.ob)) / (ibC + daC) : 1.49;
      }
      const c = r.alps ? alpsCell(ad, as, r.alps) : mainCell(ad, as, r.wad === 'ib' ? 'INBOUND' : 'DA');
      return c.WEIGHTED_AVERAGE_DENSITY || DEFAULT_WAD[r.wad] || 1.5;
    };
    const flat = r => {
      let H = 0, C = 0, wadW = 0, wadSum = 0;
      for (const [f, ad, as, ld, ls] of parts) {
        let h = 0, c = null;
        if (r.llp === 'line') { const x = alpsCell(ad, as, r.alps); h = x.SHOW_HOURS || 0; const k = cph(x, r.wad); c = k != null ? h * k : null; }
        else if (r.llp === 'ob') { h = alpsCell(ad, as, r.alps).SHOW_HOURS || 0; c = obPlanCartons(ad, as); }
        else if (r.llp === 'main') { const x = mainCell(ad, as, r.main); h = x.SHOW_HOURS || 0; c = h * (x.CPLH || 0); }
        else if (r.llp === 'prod' || r.llp === 'thru') {
          const ib = mainCell(ad, as, 'INBOUND'), da = mainCell(ad, as, 'DA');
          h = (ib.SHOW_HOURS || 0) + (da.SHOW_HOURS || 0) + (r.llp === 'thru' ? adminH(ld, ls) : 0);
          c = (ib.SHOW_HOURS || 0) * (ib.CPLH || 0) + (da.SHOW_HOURS || 0) * (da.CPLH || 0);
        } else if (r.llp === 'admin') { h = adminH(ld, ls); c = obPlanCartons(ad, as); }
        H += h * f; if (c != null) C += c * f;
        const w = wadOf(r, ad, as); wadSum += w * f; wadW += f;
      }
      const id = r.ppr || pprIds.find(x => r.pprMatch && r.pprMatch.test(x));
      const rowH = id ? PL[id] || {} : {};
      const vol = sum(winLabels.map(l => (rowH[l] || [0, 0])[0])), hrs = sum(winLabels.map(l => (rowH[l] || [0, 0])[1]));
      const wad = wadW ? wadSum / wadW : 1.5;
      return { key: r.label, label: r.label, flat: true, subs: [], llpH: H, llpCplh: H > 0 && C > 0 ? C / H : null, actH: hrs, actCplh: hrs > 0 ? vol / hrs / wad : null, noPpr: !id };
    };
    const byKey = Object.fromEntries(rows.map(r => [r.key, r]));
    const everythingRows = EVERYTHING.map(r => (r.core ? byKey[r.core] : flat(r)));
    return { rows, everythingRows, hasActuals, hasPprLines: pprIds.length > 0 };
  }, [data, alpsLi, alpsMain, lvp, date, endMin, site]);

  if (!model) return null;
  const th = { textAlign: 'right', fontSize: 12, fontWeight: 600, padding: '6px 10px', borderBottom: '2px solid #232f3e', whiteSpace: 'nowrap' };
  const td = { textAlign: 'right', fontSize: 13, padding: '5px 10px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' };
  const f1 = v => (v == null ? '—' : v.toFixed(1));
  const signCell = (v, fmt) => ({ style: { ...td, fontWeight: 700, background: v == null ? undefined : v >= 0 ? '#c6efce' : '#ffc7ce', color: v == null ? undefined : v >= 0 ? '#1e6b2f' : '#9c1b2e' }, text: v == null ? '—' : fmt(v) });
  const Cells = ({ r }) => {
    const delta = r.actCplh != null && r.llpCplh ? r.actCplh / r.llpCplh - 1 : null;
    const hrs = r.llpH != null && model.hasActuals ? r.llpH - r.actH : null;
    const d = signCell(delta, v => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`);
    const h = signCell(hrs, v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}`);
    return (
      <>
        <td style={td}>{f1(r.llpCplh)}</td><td style={td}>{f1(r.llpH)}</td>
        <td style={td}>{model.hasActuals ? f1(r.actCplh) : '—'}</td><td style={td}>{model.hasActuals ? f1(r.actH) : '—'}</td>
        <td style={d.style}>{d.text}</td><td style={h.style}>{h.text}</td>
      </>
    );
  };
  return (
    <div className="card" style={{ marginTop: 16, overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h3 className="section-title" style={{ marginTop: 0 }}>PPR Line Items vs Locked Labor Plan — {site}</h3>
        <button className="btn" onClick={() => setEverything(v => !v)} aria-pressed={everything}>{everything ? 'Show key lines only' : 'Show me everything'}</button>
      </div>
      {everything && !model.hasPprLines && <p style={{ fontSize: '0.75rem', color: '#c62828' }}>PPR line actuals are captured from the first FCLM sync after this release; this date has none yet.</p>}
      {!model.hasActuals && <p style={{ fontSize: '0.75rem', color: '#c62828' }}>Line-item actuals are captured from the first FCLM sync after this release; this date has no per-process hours.</p>}
      <table style={{ borderCollapse: 'collapse', minWidth: 760 }}>
        <thead><tr>
          <th style={{ ...th, textAlign: 'left' }}>PPR Line Item</th><th style={{ ...th, textAlign: 'left' }}>Bucket</th>
          <th style={th}>LLP CPLH</th><th style={th}>LLP Hours</th><th style={th}>Actual CPLH</th><th style={th}>Actual Hours</th><th style={th}>Delta (%)</th><th style={th}>Hours (+/-)</th>
        </tr></thead>
        <tbody>
          {(everything ? model.everythingRows : model.rows).map(r => (
            <React.Fragment key={r.key}>
              <tr style={{ background: '#f5f8ff' }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>
                  {r.subs.length > 0
                    ? <button type="button" onClick={() => setOpen(o => ({ ...o, [r.key]: !o[r.key] }))} aria-expanded={!!open[r.key]}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, padding: 0 }}>{open[r.key] ? '▾' : '▸'} {r.label}</button>
                    : r.label}
                </td>
                <td style={td} />
                <Cells r={r} />
              </tr>
              {open[r.key] && r.subs.map(s => (
                <tr key={s.path}><td style={td} /><td style={{ ...td, textAlign: 'left' }}>{s.path}</td><Cells r={s} /></tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: '0.7rem', color: '#666', margin: '8px 0 0' }}>
        LLP line items: ALPS (CPLH = UPH ÷ WAD). Transfer Out = TO Fluid Load + TO RWC + TO Palletize + TO Pallet Verify + Transfer Out Support hours, CPLH = planned OB cartons ÷ those hours.
        RSR Support CPLH = LVP planned units × 2 ÷ LLP hours. Buckets: LVP hours and rate. Actuals: FCLM. Plan prorated to the timeframe by BRT. Hours (+/-) = LLP − Actual.
        {everything && ' Everything view: other lines use ALPS line items (UPH ÷ WAD) or ALPS main process (Total Inbound, Transfer-Out); THROUGHPUT = Inbound + DA + LVP admin hours, Production Only = Inbound + DA; Admin/HR/IT and Support plan = LVP admin hours. Their actuals are the PPR row: volume ÷ hours ÷ WAD.'}
      </p>
    </div>
  );
}
